/**
 * Coverage Dropdown - Multiselect dropdown for coverage selection
 * Story 227: Edit PRDs, tech designs and tasks
 * Story 239: Display section titles instead of anchor IDs
 *
 * Features:
 * - Shows all upstream sections for coverage selection
 * - Uncovered items show warning icon
 * - Multiselect checkboxes (click area restricted to checkbox only)
 * - Section title is clickable for navigation
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import './CoverageDropdown.css';

interface CoverageDropdownProps {
  /** All available anchors to select from */
  allAnchors: string[];
  /** Currently selected anchors */
  selected: string[];
  /** Anchors that are not covered by any item (show warning icon) */
  uncovered: Set<string>;
  /** Tooltip text for uncovered items */
  uncoveredTooltip: string;
  /** Called when selection changes */
  onChange: (selected: string[]) => void;
  /** Called when item is clicked for navigation */
  onNavigate?: (anchor: string) => void;
  /** Label for the trigger button */
  label?: string;
  /** Show warning icon when selection is empty */
  showStatusBadge?: boolean;
  /** Anchors covered by OTHER items (shown with checkmark) */
  coveredByOthers?: Set<string>;
  /** Tooltip text for items covered by others */
  coveredByOthersTooltip?: string;
  /** Mapping of anchor IDs to section titles for display */
  anchorTitleMap?: Record<string, string>;
  /** Controls visibility (opacity) - for hover reveal behavior */
  visible?: boolean;
}

export function CoverageDropdown({
  allAnchors,
  selected,
  uncovered,
  uncoveredTooltip,
  onChange,
  onNavigate,
  label = 'Coverage',
  showStatusBadge = false,
  coveredByOthers = new Set(),
  coveredByOthersTooltip = 'Already covered by another item',
  anchorTitleMap = {},
  visible = true,
}: CoverageDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const toggle = useCallback(
    (anchor: string) => {
      const newSelected = selected.includes(anchor)
        ? selected.filter((a) => a !== anchor)
        : [...selected, anchor];
      onChange(newSelected);
    },
    [selected, onChange]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, anchor: string) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        toggle(anchor);
      }
    },
    [toggle]
  );

  if (allAnchors.length === 0) {
    return null;
  }

  const isEmpty = selected.length === 0;

  // Handle anchor text click for navigation
  const handleAnchorClick = useCallback(
    (e: React.MouseEvent, anchor: string) => {
      e.preventDefault();
      e.stopPropagation();
      if (onNavigate) {
        onNavigate(anchor);
        setIsOpen(false);
      }
    },
    [onNavigate]
  );

  return (
    <div
      className="coverage-dropdown"
      ref={dropdownRef}
      style={{
        opacity: visible ? 1 : 0,
        transition: 'opacity 0.3s ease',
        pointerEvents: visible ? 'auto' : 'none',
      }}
    >
      <button
        className="coverage-dropdown__trigger"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        {showStatusBadge && isEmpty && (
          <span className="coverage-dropdown__warning" title="No items selected">⚠</span>
        )}
        <span className="coverage-dropdown__label">{label}</span>
        <span className="coverage-dropdown__caret">{isOpen ? '\u25B2' : '\u25BC'}</span>
      </button>

      {isOpen && (
        <div className="coverage-dropdown__menu" role="listbox" aria-multiselectable="true">
          {allAnchors.map((anchor) => {
            const isUncovered = uncovered.has(anchor);
            const isCoveredByOther = coveredByOthers.has(anchor);
            const isSelected = selected.includes(anchor);
            // Display section title if available, fall back to anchor
            const displayTitle = anchorTitleMap[anchor] || anchor;

            return (
              <div
                key={anchor}
                className="coverage-dropdown__item"
                role="option"
                aria-selected={isSelected}
              >
                {/* Checkbox - click area restricted to checkbox only */}
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => toggle(anchor)}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => handleKeyDown(e, anchor)}
                  className="coverage-dropdown__checkbox"
                  title={isSelected ? 'Uncheck to remove' : 'Check to add'}
                />
                {/* Section title - clickable for navigation */}
                <span
                  className={`coverage-dropdown__anchor ${onNavigate ? 'coverage-dropdown__anchor--clickable' : ''}`}
                  onClick={onNavigate ? (e) => handleAnchorClick(e, anchor) : undefined}
                  title={onNavigate ? `Go to "${displayTitle}"` : undefined}
                >
                  {displayTitle}
                </span>
                {/* Status icon on the right */}
                {isUncovered && (
                  <span className="coverage-dropdown__status coverage-dropdown__status--warning" title={uncoveredTooltip}>
                    ⚠
                  </span>
                )}
                {isCoveredByOther && !isUncovered && (
                  <span className="coverage-dropdown__status coverage-dropdown__status--ok" title={coveredByOthersTooltip}>
                    ✓
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
